/* eslint-disable no-undef */
import { apiEndpoint, requestPayload, has, iFrameApiCall, isNull } from '../utils/common';
import { API, ERRORS, EVENT } from '../constant';
import { payload as _payload } from './request';
import { validateUpperCase } from '../utils/validations';
import { post } from '../utils/http';
import { pubsubLogger as log } from '../utils/logger';

/**
 * Format card value as per native api
 *
 * @param {object} card - card info object
 * @returns {string} - formatted card value
 * @private
 */
const _formatCard = card => {
  let output = '';
  if (!card) {
    return '|||'; // default value
  }
  if (has(card, 'id')) {
    output += card.id;
  }
  output += '|';
  if (has(card, 'number')) {
    output += card.number;
  }
  output += '|';
  if (has(card, 'cvv')) {
    output += card.cvv;
  }
  output += '|';
  if (has(card, 'expiry')) {
    output += card.expiry;
  }
  return output;
};
/**
 * Format card object on credit and debit card
 *
 * @param {Object} _payObj - Pay object
 * @returns {Object} - formatted pay object
 * @private
 */
const _formatObj = _payObj => {
  const payObj = _payObj;
  if (payObj.paymentMode === 'DEBIT_CARD' || payObj.paymentMode === 'CREDIT_CARD') {
    if (!has(payObj, 'cardInfo')) {
      throw new Error('Debit or Credit card must contain cardInfo object');
    }
    if (typeof payObj.cardInfo === 'object') {
      const cardInfo = _formatCard(payObj.cardInfo);
      if (has(payObj.cardInfo, 'isSave')) {
        payObj.storeInstrument = payObj.cardInfo.isSave ? '1' : '0';
      } else {
        payObj.storeInstrument = '0';
      }
      payObj.cardInfo = cardInfo;
    }
  }
  return payObj;
};

/**
 * Redirect pay request to paytm page.
 *
 * @param {object} payObj - Payment object
 * @returns {Promise<Object>} - Promise object
 */
export function pay(payObj) {
  if (!payObj) {
    return Promise.reject(new Error(ERRORS.PAY));
  }
  const payload = { ...payObj };
  payload.requestType = 'NATIVE';

  return post.call(this, apiEndpoint(API.PT), requestPayload(_formatObj(payObj)));

  // openWindowPopup(this.cache);
  // return new Promise((resolve, reject) => {
  //   getValidTokenForFlow(this.cache)
  //     .then(_ => {
  //       _pay.call(this, payload).then(
  //         res => {
  //           resolve(res);
  //         },
  //         err => {
  //           reject(err);
  //         }
  //       );
  //     })
  //     .catch(err => {
  //       reject(err);
  //     });
  // });

  // return _pay(payObj);
}

// function _pay(payObj) {
//   return post.call(this, apiEndpoint(API.PT), requestPayload(_formatObj(payObj)));
// };

/**
 * Fetch balance of paytm payments instrument ( Wallet , Payment Bank , Postpaid )
 *
 * @param {string} payMode - Paymode for which balance fetch. Allow values ( BALANCE | PPBL | PAYTM_DIGITAL_CREDIT )
 * @returns {Promise<Object>} - Promise object
 */
export function balance(payMode) {
  if (!validateUpperCase(payMode)) {
    return Promise.reject(new Error(ERRORS.BAL));
  }

  return new Promise((resolve, reject) => {
    const payload = _payload.call(this, API.BAL);
    payload.data.body = { ...payload.data.body, paymentMode: payMode };
    iFrameApiCall(payload)
      .then(data => {
        resolve(data);
      })
      .catch(err => {
        reject(err);
      });
  });
  // return post(apiEndpoint(API.BAL), requestPayload({ paymentMode: payMode }));
}

/**
 * Fetch all payments options available on merchant
 *
 * @returns {Promise<Object>} - Promise object
 */
export function payModes() {
  return post.call(this, apiEndpoint(API.OPTIONS), requestPayload({}));
}

/**
 * Transaction status of order
 * @param {String} mid - merchant id
 * @param {String} orderId - order id
 * @returns {Promise<Object>} - Promise object
 */
export function status(mid, orderId) {
  if (isNull(mid)) {
    return Promise.reject(new Error(ERRORS.MID));
  }

  if (isNull(orderId)) {
    return Promise.reject(new Error(ERRORS.ORDERID));
  }

  return post.call(
    this,
    apiEndpoint(API.TRANS_STATUS),
    requestPayload({ mid, orderId, isCallbackUrlRequired: true })
  );
}

// filter vpas from paymode
const _filterPaymodes = paymodes => {
  paymodes.bankAccounts.map(paymode => {
    paymode.bankMetaData = {};
    paymode.credsAllowed = [];
    paymode.accRefNumber = '';
    paymode.name = '';
    paymode.account = '';
    paymode.accRefId = '';
    paymode.ifsc = '';
    paymode.mpinSet = '';
    return paymode;
  });

  paymodes.vpaDetails.map(paymode => {
    paymode.name = '';
    if (paymode.defaultDebit) {
      paymode.defaultDebit.account = '';
      paymode.defaultDebit.accRefNumber = '';
      paymode.defaultDebit.accRefId = '';
      paymode.defaultDebit.ifsc = '';
      paymode.defaultDebit.credsAllowed = [];
    }
    if (paymode.defaultCredit) {
      paymode.defaultCredit.account = '';
      paymode.defaultCredit.accRefNumber = '';
      paymode.defaultCredit.accRefId = '';
      paymode.defaultCredit.ifsc = '';
      paymode.defaultCredit.credsAllowed = [];
    }

    return paymode;
  });
  return paymodes;
};

// mapping app data for ui logger to remove user info
const _mapAppDataLog = appData => {
  let _data = {};
  const localAppData = JSON.parse(JSON.stringify(appData.body));
  if (appData) {
    _data = localAppData;
    _data.userDetails = localAppData.userDetails && {
      ...localAppData.userDetails,
      username: '',
      mobile: '',
      email: ''
    };
    _data.upiMetaData = localAppData.upiMetaData && {
      ...localAppData.upiMetaData,
      upiLinkedMobileNumber: ''
    };
    _data.merchantDetails = localAppData.merchantDetails && {
      ...localAppData.merchantDetails,
      merchantVpa: ''
    };
    _data.addMoneyMerchantDetails = localAppData.addMoneyMerchantDetails && {
      ...localAppData.addMoneyMerchantDetails,
      merchantVpa: ''
    };

    if (_data.merchantPayOption && _data.merchantPayOption.userProfileSarvatra) {
      _data.merchantPayOption.userProfileSarvatra.response = localAppData.merchantPayOption
        .userProfileSarvatra
        ? _filterPaymodes(localAppData.merchantPayOption.userProfileSarvatra.response)
        : '';
    }

    if (_data.addMoneyPayOption && _data.addMoneyPayOption.userProfileSarvatra) {
      _data.addMoneyPayOption.userProfileSarvatra.response = localAppData.addMoneyPayOption
        .userProfileSarvatra
        ? _filterPaymodes(localAppData.addMoneyPayOption.userProfileSarvatra.response)
        : '';
    }

    delete _data.retryData;
  }
  return _data;
};

export function tokenPaymodes(payload) {
  const {
    token,
    mid,
    orderId,
    applyPaymentOffer,
    fetchAllPaymentOffers,
    referenceId,
    requestId,
    tokenType
  } = payload;
  return new Promise((resolve, reject) => {
    const payload = _payload.call(this, API.OPTIONS);
    payload.data.head = { ...payload.data.head, token, tokenType, type: tokenType };
    payload.data.body = {
      ...payload.data.body,
      mid,
      orderId,
      applyPaymentOffer,
      fetchAllPaymentOffers
    };
    /* If request Id found add it to body params */
    if (requestId) {
      payload.data.body.requestId = requestId;
    }
    payload.query = { ...payload.query, orderId };
    /* If reference Id found add it to query params */
    if (referenceId) {
      payload.query = { ...payload.query, referenceId };
    }

    log.info({ eventType: EVENT.API_INIT, url: payload.url, iframeCall: true });
    iFrameApiCall(payload)
      .then(data => {
        try {
          const parseData = _mapAppDataLog(JSON.parse(JSON.stringify(data)));

          log.info({
            eventType: EVENT.API_STATUS,
            url: payload.url,
            data: parseData,
            statusCode: 200,
            iframe: true
          });
        } catch (e) {}
        resolve(data);
      })
      .catch(err => {
        log.info({
          eventType: EVENT.API_STATUS,
          url: payload.url,
          data: err,
          statusCode: 500,
          iframe: true
        });
        reject(err);
      });
  });
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Preprod-BnR/Deploy_checkoutjs/common/src/api/paytm.js